import { useState, useMemo, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { safeFormat } from "@/lib/dateUtils";
import { useAuth } from "@/_core/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { showOperationSuccess } from "@/lib/operationFeedback";
import { useLocation } from "wouter";
import { getMobileProfessionalTurnCountdown } from "@shared/mobileOperations";
import {
  Calendar, Clock, User, Loader2, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, AlertCircle, Scissors,
} from "lucide-react";
import { format, addDays, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  completed: "bg-blue-100 text-blue-800",
  no_show: "bg-gray-100 text-gray-800",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmado",
  cancelled: "Cancelado",
  completed: "Concluído",
  no_show: "Não compareceu",
};

export default function StaffDashboard() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [selectedProfId, setSelectedProfId] = useState<number | null>(null);
  const [turnClock, setTurnClock] = useState(() => new Date());

  const dateInput = useMemo(() => ({ date: selectedDate }), [selectedDate]);

  const { data, isLoading } = trpc.appointments.myTodayAppointments.useQuery(dateInput);
  const { data: queue = [] } = trpc.queue.list.useQuery(undefined, { refetchInterval: 10_000 });

  useEffect(() => {
    const interval = window.setInterval(() => setTurnClock(new Date()), 30_000);
    return () => window.clearInterval(interval);
  }, []);

  const updateStatus = trpc.appointments.updateStatus.useMutation({
    onSuccess: (_data, variables) => {
      const label = STATUS_LABELS[variables.status] ?? variables.status;
      showOperationSuccess({
        title: "Agendamento atualizado",
        description: `O status foi alterado para “${label}” e já está refletido na agenda do dia.`,
      });
      utils.appointments.myTodayAppointments.invalidate();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const navigateDay = (dir: -1 | 1) => {
    setSelectedDate((prev) => startOfDay(addDays(prev, dir)));
  };

  const isToday = selectedDate.getTime() === startOfDay(new Date()).getTime();
  const dayLabel = format(selectedDate, "EEEE, dd 'de' MMMM", { locale: ptBR });

  // Se não encontrou profissional vinculado, mostrar seletor manual
  const allProfessionals = (data as { allProfessionals?: Array<{ id: number; name: string }> } | undefined)?.allProfessionals;
  const professional = data?.professional;
  const hasProfessional = !!professional;

  // Usar profissional selecionado manualmente ou o vinculado automaticamente
  const effectiveProfId = selectedProfId ?? professional?.id ?? null;

  // Buscar agendamentos do profissional selecionado manualmente
  const { data: manualProfData, isLoading: manualLoading } = trpc.appointments.myTodayAppointments.useQuery(
    dateInput,
    { enabled: !hasProfessional && !!selectedProfId }
  );

  const appointments = hasProfessional
    ? data?.appointments ?? []
    : (manualProfData?.appointments ?? []);

  const isLoadingAny = isLoading || manualLoading;

  // Stats do dia
  const pending = appointments.filter((a) => a.status === "pending").length;
  const confirmed = appointments.filter((a) => a.status === "confirmed").length;
  const completed = appointments.filter((a) => a.status === "completed").length;
  const total = appointments.length;
  const nextAppointment = data?.nextAppointment ?? appointments
    .filter((appointment) => appointment.status === "pending" || appointment.status === "confirmed")
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0];
  const nextQueueItem = nextAppointment
    ? (queue as any[]).find((item) => item.appointmentId === nextAppointment.id && item.status !== "done")
    : undefined;
  const turnCountdown = nextAppointment
    ? getMobileProfessionalTurnCountdown(nextAppointment.startTime, turnClock)
    : null;
  const isServiceInProgress = nextQueueItem?.status === "in_service";

  const profName = professional?.name ?? user?.name ?? "Profissional";

  const updateQueueStatus = trpc.queue.updateStatus.useMutation({
    onSuccess: () => utils.queue.list.invalidate(),
    onError: (error: { message: string }) => toast.error(error.message),
  });

  const startMyTurn = async () => {
    if (!nextAppointment || !nextQueueItem) return;
    try {
      if (nextAppointment.status === "pending") {
        await updateStatus.mutateAsync({ id: nextAppointment.id, status: "confirmed" });
      }
      await updateQueueStatus.mutateAsync({ id: nextQueueItem.id, status: "in_service" });
      showOperationSuccess({
        title: "Atendimento iniciado",
        description: `${nextAppointment.clientName ?? "Cliente"} está em atendimento com você.`,
      });
    } catch {
      // As mutações já exibem uma mensagem clara de erro.
    }
  };

  const finishMyTurn = async () => {
    if (!nextAppointment) return;
    try {
      await updateStatus.mutateAsync({ id: nextAppointment.id, status: "completed" });
      if (nextQueueItem) {
        await updateQueueStatus.mutateAsync({ id: nextQueueItem.id, status: "done" });
      }
      showOperationSuccess({
        title: "Atendimento concluído",
        description: "A agenda e as comissões foram atualizadas.",
      });
    } catch {
      // As mutações já exibem uma mensagem clara de erro.
    }
  };

  return (
    <div className="space-y-5">
      {/* Minha operação agora */}
      <section className="rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-amber-50/60 p-4 sm:p-6">
        <Badge variant="outline" className="mb-2 border-primary/25 bg-background/80 text-primary">Profissional</Badge>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Minha operação agora</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Olá, {user?.name?.split(" ")[0] ?? "Profissional"}. {hasProfessional ? `Seus agendamentos como ${profName}` : "Selecione seu perfil de profissional abaixo."}
        </p>
        {nextAppointment && (
          <div className="mt-4 rounded-xl border border-border bg-background/85 p-3">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1"><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Minha vez agora · Próximo atendimento</p><p className="truncate text-sm font-semibold">{safeFormat(nextAppointment.startTime, "dd/MM · HH:mm")} · {nextAppointment.clientName ?? "Cliente"}</p><p className="truncate text-xs text-muted-foreground">{nextAppointment.serviceName ?? "Serviço agendado"}</p></div>
              {turnCountdown && <Badge className={turnCountdown.tone === "active" ? "bg-blue-600 text-white" : turnCountdown.tone === "urgent" ? "bg-amber-500 text-white" : "bg-secondary text-secondary-foreground"}>{turnCountdown.label}</Badge>}
            </div>
            <div className="mt-3 border-t border-border/60 pt-3">
              {isServiceInProgress ? (
                <Button className="min-h-12 w-full bg-blue-600 text-white hover:bg-blue-700" onClick={finishMyTurn} disabled={updateStatus.isPending || updateQueueStatus.isPending}>
                  <CheckCircle className="mr-2 h-4 w-4" /> Concluir atendimento
                </Button>
              ) : nextQueueItem ? (
                <Button className="min-h-12 w-full bg-primary text-primary-foreground" onClick={startMyTurn} disabled={updateStatus.isPending || updateQueueStatus.isPending}>
                  <Scissors className="mr-2 h-4 w-4" /> Iniciar atendimento
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">Aguardando a chegada da cliente ser registrada pela recepção. O início do atendimento respeita a ordem de chegada.</p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Seletor de profissional (quando não há vínculo automático) */}
      {!hasProfessional && !isLoading && allProfessionals && allProfessionals.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 mb-2">
                  Seu nome não corresponde a nenhum profissional cadastrado. Selecione manualmente:
                </p>
                <Select
                  value={selectedProfId?.toString() ?? ""}
                  onValueChange={(v) => setSelectedProfId(parseInt(v, 10))}
                >
                  <SelectTrigger className="w-full max-w-xs bg-white">
                    <SelectValue placeholder="Selecionar profissional..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allProfessionals.map((p) => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navegação de data */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateDay(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-center">
          <p className="text-sm font-semibold text-foreground capitalize">{dayLabel}</p>
          {isToday && <p className="text-xs text-primary font-medium">Hoje</p>}
        </div>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateDay(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        {!isToday && (
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setSelectedDate(startOfDay(new Date()))}>
            Voltar para hoje
          </Button>
        )}
        {isToday && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setSelectedDate(startOfDay(addDays(new Date(), 1)))}>
            Amanhã
          </Button>
        )}
      </div>

      {/* Cards de resumo do dia */}
      {(hasProfessional || selectedProfId) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total", value: total, color: "text-foreground", bg: "bg-secondary" },
            { label: "Pendentes", value: pending, color: "text-yellow-700", bg: "bg-yellow-50" },
            { label: "Confirmados", value: confirmed, color: "text-green-700", bg: "bg-green-50" },
            { label: "Concluídos", value: completed, color: "text-blue-700", bg: "bg-blue-50" },
          ].map((stat) => (
            <Card key={stat.label} className="border-border/40">
              <CardContent className={`p-3 ${stat.bg} rounded-lg`}>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <section>
        <div className="mb-3">
          <h2 className="text-base font-semibold">Ações do dia</h2>
          <p className="text-xs text-muted-foreground">Acesse rapidamente as ferramentas do seu turno.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button variant="outline" className="h-auto min-h-16 justify-start gap-3 p-3 text-left" onClick={() => navigate("/weekly-calendar")}>
            <Calendar className="w-5 h-5 text-primary shrink-0" />
            <span><span className="block text-sm font-medium">Minha agenda</span><span className="block text-xs font-normal text-muted-foreground">Ver seus horários</span></span>
          </Button>
          <Button variant="outline" className="h-auto min-h-16 justify-start gap-3 p-3 text-left" onClick={() => navigate("/clock-in")}>
            <Clock className="w-5 h-5 text-primary shrink-0" />
            <span><span className="block text-sm font-medium">Bater ponto</span><span className="block text-xs font-normal text-muted-foreground">Registrar entrada ou saída</span></span>
          </Button>
          <Button variant="outline" className="h-auto min-h-16 justify-start gap-3 p-3 text-left" onClick={() => navigate("/my-commissions")}>
            <CheckCircle className="w-5 h-5 text-primary shrink-0" />
            <span><span className="block text-sm font-medium">Minhas comissões</span><span className="block text-xs font-normal text-muted-foreground">Consultar seus valores</span></span>
          </Button>
        </div>
      </section>

      {/* Lista de agendamentos */}
      {isLoadingAny ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : !hasProfessional && !selectedProfId ? (
        <div className="text-center py-12 text-muted-foreground">
          <Scissors className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Selecione seu perfil de profissional para ver seus agendamentos.</p>
        </div>
      ) : appointments.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Nenhum agendamento para este dia.</p>
          <p className="text-xs mt-1">Aproveite o dia livre! 🌟</p>
        </div>
      ) : (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            {appointments.length} agendamento{appointments.length !== 1 ? "s" : ""} do dia
          </h2>
          {appointments
            .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
            .map((appt) => (
              <Card key={appt.id} className="border-border/50 hover:border-primary/30 transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3">
                      {/* Horário em destaque */}
                      <div className="text-center min-w-[52px]">
                        <p className="text-lg font-bold text-primary leading-none">
                          {safeFormat(appt.startTime, "HH:mm")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {safeFormat(appt.endTime, "HH:mm")}
                        </p>
                      </div>
                      <div className="w-px h-10 bg-border/50 self-center" />
                      <div>
                        <p className="font-semibold text-foreground">
                          {appt.clientName ?? "Cliente"}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Scissors className="w-3 h-3" />
                            {appt.serviceName ?? `Serviço #${appt.serviceId}`}
                          </div>
                          {appt.clientPhone && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <User className="w-3 h-3" /> {appt.clientPhone}
                            </div>
                          )}
                        </div>
                        {appt.notes && (
                          <p className="text-xs text-muted-foreground mt-1 italic">"{appt.notes}"</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={`text-xs ${STATUS_COLORS[appt.status]}`}>
                        {STATUS_LABELS[appt.status]}
                      </Badge>
                      {appt.status !== "cancelled" && appt.status !== "completed" && (
                        <div className="flex gap-1.5">
                          {appt.status === "pending" && (
                            <Button
                              size="sm"
                              className="h-10 text-xs bg-green-600 hover:bg-green-700 text-white sm:h-7"
                              onClick={() => updateStatus.mutate({ id: appt.id, status: "confirmed" })}
                              disabled={updateStatus.isPending}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" /> Confirmar
                            </Button>
                          )}
                          {appt.status === "confirmed" && (
                            <Button
                              size="sm"
                              className="h-10 text-xs bg-blue-600 hover:bg-blue-700 text-white sm:h-7"
                              onClick={() => updateStatus.mutate({ id: appt.id, status: "completed" })}
                              disabled={updateStatus.isPending}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" /> Concluir
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-10 text-xs text-destructive border-destructive/30 sm:h-7"
                            onClick={() => updateStatus.mutate({ id: appt.id, status: "no_show" })}
                            disabled={updateStatus.isPending}
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Faltou
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
}
